import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Alert,
  IconButton,
  Snackbar,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import authStore from '../stores/AuthStore';
import Logo from '../assets/images/full-logo-white.png';

const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/;

const ResetPasswordPage = () => {
  const [formData, setFormData] = useState({ password: '', confirm: '' });
  const [errors, setErrors] = useState({});
  const [submissionError, setSubmissionError] = useState(null);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();

  const token = searchParams.get('token');

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const v = {};
    if (!passwordRegex.test(formData.password)) {
      v.password = 'Password must be at least 8 characters and include a number, an uppercase letter, and a special character.';
    }
    if (formData.confirm !== formData.password) {
      v.confirm = 'Passwords do not match.';
    }
    setErrors(v);
    return Object.keys(v).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!token) {
      setSubmissionError('This reset link is invalid or has expired.');
      return;
    }
    if (!validate()) return;

    try {
      await authStore.resetPassword(token, formData.password);
      setDone(true);
      setTimeout(() => navigate('/sign-in'), 2500);
    } catch (error) {
      setSubmissionError('Failed to reset password: ' + (error?.message || 'please try again.'));
    }
  };

  return (
    <Box component="main" sx={{
      backgroundImage: `url(${require('../assets/images/physics.png')})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      height: '100vh',
      width: '100vw',
      maxWidth: '100vw',
      overflowX: 'hidden',
    }}>
      <Container maxWidth="sm" sx={{
        display: 'flex',
        justifyContent: 'start',
        alignItems: 'center',
        flexDirection: 'column',
        height: '100%',
      }}>
        <Box sx={{ m: 2 }}>
          <img src={Logo} alt="Ear Physics Logo" style={{ width: '200px', height: 'auto' }} />
        </Box>
        <Box
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(5px)',
            borderRadius: 2,
            p: 4,
            width: '100%',
            maxWidth: 'sm',
          }}
        >
          <Box sx={{ mb: 2, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <IconButton color="inherit" component={Link} to="/sign-in">
              <ArrowBackIcon />
            </IconButton>
            <Typography
              component="h1"
              variant="h5"
              gutterBottom
              sx={{ color: theme.palette.text.primary, flex: 1, textAlign: 'center', mr: 4 }}
            >
              Reset Password
            </Typography>
          </Box>

          {submissionError && (
            <Snackbar open={true} autoHideDuration={6000} onClose={() => setSubmissionError(null)}>
              <Alert severity="error" onClose={() => setSubmissionError(null)}>{submissionError}</Alert>
            </Snackbar>
          )}

          {done ? (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                Password updated.
              </Typography>
              <Typography variant="body1" sx={{ mt: 1, color: theme.palette.text.secondary }}>
                Redirecting you to sign in…
              </Typography>
            </Box>
          ) : !token ? (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                This reset link is invalid or has expired. Please request a new one.
              </Typography>
              <Button
                fullWidth
                variant="text"
                component={Link}
                to="/forgot-password"
                sx={{ mt: 2, color: theme.palette.text.primary }}
              >
                Request a new reset link
              </Button>
            </Box>
          ) : (
            <>
              <Typography component="p" variant="body1" align="center" sx={{ mb: 2, color: theme.palette.text.secondary }}>
                Enter a new password for your account.
              </Typography>
              <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.primary, mb: 1 }}>
                  New Password
                </Typography>
                <TextField
                  margin="dense"
                  required
                  fullWidth
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  error={Boolean(errors.password)}
                  placeholder="Enter a new password"
                  helperText={errors.password}
                  InputProps={{
                    style: {
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                    },
                  }}
                />
                <Typography variant="body2" sx={{ color: theme.palette.text.primary, mt: 2 }}>
                  Confirm Password
                </Typography>
                <TextField
                  margin="dense"
                  required
                  fullWidth
                  id="confirm"
                  name="confirm"
                  type="password"
                  value={formData.confirm}
                  onChange={handleChange}
                  error={Boolean(errors.confirm)}
                  placeholder="Re-enter your new password"
                  helperText={errors.confirm}
                  InputProps={{
                    style: {
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                    },
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="outlined"
                  sx={{
                    mt: 3,
                    mb: 2,
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    '&:hover': { backgroundColor: theme.palette.primary.dark },
                  }}
                >
                  Reset Password
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default ResetPasswordPage;
